<template>
  <section class="relative pt-24 pb-16 md:pt-28 md:pb-24 bg-[#1E2652] min-h-screen flex items-center justify-center">
    <div class="container mx-auto px-4 md:px-6 max-w-4xl text-center">
      <div class="bg-white/[0.04] border border-white/10 rounded-2xl p-8 md:p-12">
        <div v-if="isRedirecting" class="space-y-6">
          <div class="flex justify-center">
            <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#B7E000]"></div>
          </div>
          <h1 class="text-2xl md:text-3xl font-bold text-white">
            Redirection en cours...
          </h1>
          <p class="text-gray-300">
            Vous allez être redirigé vers le store approprié.
          </p>
        </div>
        <div v-else class="space-y-6">
          <h1 class="text-2xl md:text-3xl font-bold text-white mb-4">
            Télécharger PadelGo
          </h1>
          <p class="text-gray-300 mb-8">
            Choisissez votre plateforme pour télécharger l'application
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              :href="iosStoreUrlWeb"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-[#B7E000] text-[#1E2652] font-semibold hover:bg-[#B7E000]/90 transition-colors min-w-[200px]"
            >
              <svg class="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              App Store
            </a>
            <a
              :href="androidStoreUrlWeb"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-[#B7E000] text-[#1E2652] font-semibold hover:bg-[#B7E000]/90 transition-colors min-w-[200px]"
            >
              <svg class="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.19,15.12L14.54,12.85L17.19,10.81L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Google Play
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

// URL pour iOS avec schéma itms-apps:// pour déclencher le bottom sheet natif
const iosStoreUrlNative = 'itms-apps://itunes.apple.com/app/id6743053827'
const iosStoreUrlWeb = 'https://apps.apple.com/fr/app/padelgo/id6743053827'
// URL pour Android avec schéma market:// pour ouvrir directement l'app Play Store
const androidStoreUrlNative = 'market://details?id=com.rmarsile.padelgo'
const androidStoreUrlWeb = 'https://play.google.com/store/apps/details?id=com.rmarsile.padelgo&hl=fr'

const isRedirecting = ref(false)

function detectDevice(): 'ios' | 'android' | 'other' {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera

  // Détection iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios'
  }

  // Détection Android
  if (/android/i.test(userAgent)) {
    return 'android'
  }

  return 'other'
}

onMounted(() => {
  const device = detectDevice()
  
  if (device === 'ios') {
    isRedirecting.value = true
    // Utilisation du schéma itms-apps:// pour déclencher le bottom sheet natif de l'App Store
    // Ce schéma ouvre directement l'App Store avec le panneau natif qui s'ouvre depuis le bas
    window.location.href = iosStoreUrlNative
  } else if (device === 'android') {
    isRedirecting.value = true
    // Utilisation du schéma market:// pour ouvrir directement l'app Play Store
    // Si l'app n'est pas installée, le navigateur redirigera vers le lien HTTPS
    window.location.href = androidStoreUrlNative
  }
  // Si c'est un autre appareil (desktop, etc.), on affiche les deux options
})
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

